import { Capacitor } from "@capacitor/core";
import { supabase } from "./supabase";
import {
  derivedLayerMapAlias,
  getDerivedLayerMapAccess,
  THERA_DERIVED_ANDROID_OFFLINE_ENABLED,
  type ActiveDerivedLayerRelease,
} from "./derived-layer-release";

export type StoredDerivedLayerRelease = {
  key: string;
  owner_user_id: string;
  release_id: string;
  region_id: string;
  layer: string;
  version: number;
  alias: string;
  size_bytes: number;
  sha256: string;
  downloaded_at: string;
  blob: Blob;
};

const DATABASE = "thera-derived-layer-offline";
const STORE = "releases";
const DATABASE_VERSION = 1;

export function derivedLayerOfflineRuntimeEnabled() {
  return THERA_DERIVED_ANDROID_OFFLINE_ENABLED && Capacitor.getPlatform() === "android";
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function currentSessionUserId() {
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data.session?.user.id ?? null;
}

function storageKey(userId: string, regionId: string, layer: string) {
  return `${userId}:${regionId}:${layer}`;
}

async function readRecord(key: string) {
  const database = await openDatabase();
  const record = await new Promise<StoredDerivedLayerRelease | undefined>((resolve, reject) => {
    const request = database.transaction(STORE, "readonly").objectStore(STORE).get(key);
    request.onsuccess = () => resolve(request.result as StoredDerivedLayerRelease | undefined);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return record;
}

async function writeRecord(record: StoredDerivedLayerRelease) {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function getStoredDerivedLayerForCurrentSession(regionId: string, layer: string) {
  if (!derivedLayerOfflineRuntimeEnabled()) return null;
  const userId = await currentSessionUserId();
  if (!userId) return null;
  const record = await readRecord(storageKey(userId, regionId, layer));
  if (!record || record.owner_user_id !== userId) return null;
  if (!(record.blob instanceof Blob) || record.blob.size <= 0) return null;
  return record;
}

export async function removeStoredDerivedLayerForCurrentSession(regionId: string, layer: string) {
  const userId = await currentSessionUserId();
  if (!userId) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).delete(storageKey(userId, regionId, layer));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

async function sha256Hex(blob: Blob) {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function responseToBlob(response: Response, onProgress: (value: number) => void) {
  const expected = Number(response.headers.get("Content-Length") ?? 0);
  const reader = response.body?.getReader();
  if (!reader) {
    const blob = await response.blob();
    onProgress(100);
    return blob;
  }

  const chunks: Uint8Array[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.byteLength;
      if (expected > 0) onProgress(Math.min(99, Math.round((received / expected) * 100)));
    }
  }
  onProgress(100);
  const mergedBuffer = new ArrayBuffer(received);
  const mergedView = new Uint8Array(mergedBuffer);
  let offset = 0;
  for (const chunk of chunks) {
    mergedView.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new Blob([mergedBuffer], { type: "application/vnd.pmtiles" });
}

export async function downloadDerivedLayerForOffline(
  release: ActiveDerivedLayerRelease,
  onProgress: (value: number) => void = () => undefined,
): Promise<StoredDerivedLayerRelease> {
  if (!derivedLayerOfflineRuntimeEnabled()) {
    throw new Error("PHASE3K offline solo está habilitado en Android.");
  }
  if (!navigator.onLine) {
    throw new Error("Conéctate una vez para descargar Field Survey V1.");
  }
  if (!supabase) throw new Error("Supabase no está configurado.");

  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new Error("Se requiere una sesión autenticada.");
  const ownerUserId = data.session.user.id;
  const access = await getDerivedLayerMapAccess(release);

  const response = await fetch(access.url, {
    method: "GET",
    headers: access.headers,
    cache: "no-store",
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(problem?.error ?? `Gateway de capa derivada respondió HTTP ${response.status}.`);
  }

  const advertisedLength = Number(response.headers.get("Content-Length") ?? 0);
  if (advertisedLength > 0 && advertisedLength !== release.size_bytes) {
    throw new Error("El tamaño remoto no coincide con el release activo.");
  }
  if (release.size_bytes > 0 && navigator.storage?.estimate) {
    const { quota = 0, usage = 0 } = await navigator.storage.estimate();
    if (quota > 0 && quota - usage < release.size_bytes * 1.5) {
      throw new Error("No existe espacio suficiente para guardar la capa derivada.");
    }
  }

  const blob = await responseToBlob(response, onProgress);
  if (blob.size !== release.size_bytes) {
    throw new Error(`PMTiles derivado incompleto: ${blob.size}/${release.size_bytes} bytes.`);
  }

  const actualSha256 = await sha256Hex(blob);
  if (actualSha256.toLowerCase() !== release.sha256.toLowerCase()) {
    throw new Error("SHA-256 del PMTiles derivado no coincide con el release activo.");
  }

  await navigator.storage?.persist?.().catch(() => false);
  const record: StoredDerivedLayerRelease = {
    key: storageKey(ownerUserId, release.region_id, release.layer),
    owner_user_id: ownerUserId,
    release_id: release.release_id,
    region_id: release.region_id,
    layer: release.layer,
    version: release.version,
    alias: derivedLayerMapAlias(release),
    size_bytes: release.size_bytes,
    sha256: release.sha256.toLowerCase(),
    downloaded_at: new Date().toISOString(),
    blob,
  };

  await writeRecord(record);
  const verified = await readRecord(record.key);
  if (!verified || verified.blob.size !== record.size_bytes || verified.sha256 !== record.sha256) {
    throw new Error("La capa derivada no pudo verificarse después de guardarla.");
  }
  return verified;
}
